import { useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { useUserStats } from "../lib/UserStatsContext";
import ProfileAccountTab from "./ProfileAccountTab";
import ProfilePreferencesTab from "./ProfilePreferencesTab";
import ProfilePrivacyTab from "./ProfilePrivacyTab";
import ProfileActivityTab from "./ProfileActivityTab";
import ContactForm from "./ContactForm";

type ProfileTab = "account" | "preferences" | "privacy" | "activity";

const PROFILE_TABS: { id: ProfileTab; label: string }[] = [
  { id: "account", label: "Account" },
  { id: "preferences", label: "Preferences" },
  { id: "privacy", label: "Privacy" },
  { id: "activity", label: "Activity" },
];

function getCheckoutMessage(): { type: "success" | "cancelled"; text: string } | null {
  const params = new URLSearchParams(window.location.search);
  const status = params.get("checkout");
  if (status === "success" || status === "cancelled") {
    window.history.replaceState({}, "", window.location.pathname);
    return status === "success"
      ? { type: "success", text: "Welcome to Premium! Your subscription is now active." }
      : { type: "cancelled", text: "Checkout cancelled. No charges were made." };
  }
  return null;
}

export default function Profile() {
  const { user } = useAuth();
  const { stats, loading, error, refreshStats } = useUserStats();
  const [profileTab, setProfileTab] = useState<ProfileTab>("account");
  const [showContact, setShowContact] = useState(false);
  const [checkoutMessage] = useState(getCheckoutMessage);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <p className="text-muted mb-4">{error}</p>
          <button
            onClick={refreshStats}
            className="px-4 py-2 bg-accent text-white rounded-md font-[family-name:--font-ui] text-sm font-medium hover:opacity-85 active:opacity-75"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4">
      {/* Profile Header */}
      <div className="border-b border-border-light pb-6 mb-6">
        <div className="flex items-center gap-4">
          {user?.photoURL ? (
            <img src={user.photoURL} alt={user.displayName || "User"} className="w-12 h-12 rounded-full" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-accent/10 text-accent flex items-center justify-center font-[family-name:--font-ui] font-semibold text-lg">
              {user?.displayName?.[0] || "U"}
            </div>
          )}
          <div>
            <h2 className="text-xl font-semibold text-text">{user?.displayName}</h2>
            {user?.email && (
              <p className="text-sm text-muted font-[family-name:--font-ui]">{user.email}</p>
            )}
            <p className="text-xs text-muted font-[family-name:--font-ui]">
              Member since {stats?.joinedAt ? new Date(stats.joinedAt).toLocaleDateString() : "today"}
            </p>
          </div>
        </div>
      </div>

      {/* Profile Tabs */}
      <div className="flex gap-4 overflow-x-auto border-b border-border mb-6">
        {PROFILE_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setProfileTab(tab.id)}
            className={`flex-shrink-0 pb-2 text-sm font-[family-name:--font-ui] font-medium transition-colors ${
              profileTab === tab.id
                ? "text-accent border-b-2 border-accent"
                : "text-muted hover:text-text"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {profileTab === "account" && <ProfileAccountTab checkoutMessage={checkoutMessage} />}
      {profileTab === "preferences" && <ProfilePreferencesTab />}
      {profileTab === "privacy" && <ProfilePrivacyTab />}
      {profileTab === "activity" && <ProfileActivityTab />}

      {/* Feedback — always visible */}
      <div className="border-t border-border-light pt-4 mt-6 text-center">
        <button
          onClick={() => setShowContact(true)}
          className="text-sm text-accent font-[family-name:--font-ui] font-medium hover:underline"
        >
          Send Feedback
        </button>
      </div>

      {showContact && <ContactForm onClose={() => setShowContact(false)} userId={user?.uid} />}
    </div>
  );
}
